import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import api from '../../lib/api'
import { Check, Clock, Phone, MessageSquare, Brain, BarChart3 } from 'lucide-react'
import Wordmark from '@/components/layout/Wordmark'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import CreateAccountStep from './CreateAccountStep'
import OrganizationSetupStep from './OrganizationSetupStep'
import PlanSelectStep from './PlanSelectStep'
import CompleteStep from './CompleteStep'
import VerifyEmailStep from './VerifyEmailStep'

interface OnboardingProgress {
  currentStep: number
  completedSteps: number[]
  data: {
    account?: {
      email: string
      name: string
    }
    organization?: {
      id: string
      name: string
      slug: string
      website?: string
      industry?: string
    }
    branding?: {
      logo?: string
      primaryColor?: string
    }
    widget?: {
      configured: boolean
    }
    voice?: {
      configured: boolean
    }
    integrations?: {
      configured: boolean
    }
  }
}

const steps = [
  { id: 1, name: 'Account', description: 'Create your account' },
  { id: 2, name: 'Organization', description: 'Set up your organization' },
  { id: 3, name: 'Plan', description: 'Choose your coverage' },
  { id: 4, name: 'Complete', description: 'Launch your account' },
]

const STEP_DATA_KEY = 'app_onboarding_data'

function loadStepData(): Record<string, any> {
  try {
    const saved = localStorage.getItem(STEP_DATA_KEY)
    return saved ? JSON.parse(saved) : {}
  } catch { return {} }
}

function saveStepData(data: Record<string, any>) {
  localStorage.setItem(STEP_DATA_KEY, JSON.stringify(data))
}

export default function OnboardingPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [currentStep, setCurrentStep] = useState(() => {
    // Parse step from URL: /onboarding/step/3 → 3. Onboarding is now 4 steps
    // (Account → Org → Plan → Complete). Legacy URLs (step/5+) clamp to 4.
    const match = location.pathname.match(/\/onboarding\/step\/(\d+)/)
    const parsed = match ? parseInt(match[1]) : 0
    if (parsed >= 1 && parsed <= 4) return parsed
    if (parsed > 4) return 4
    return 1
  })
  const [completedSteps, setCompletedSteps] = useState<number[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [progress, setProgress] = useState<OnboardingProgress | null>(null)
  const [stepData, setStepData] = useState<Record<string, any>>(loadStepData)
  const [showSaveDialog, setShowSaveDialog] = useState(false)
  // null = unknown, true = verified (or grandfathered), false = pending click from email
  const [emailVerified, setEmailVerified] = useState<boolean | null>(null)

  // Sync URL with current step
  useEffect(() => {
    const expectedPath = `/onboarding/step/${currentStep}`
    if (window.location.pathname !== expectedPath && !isLoading) {
      navigate(expectedPath, { replace: true })
    }
  }, [currentStep, isLoading])

  // Reset scroll on step change — otherwise a long step 2 leaves the user
  // partway down the page when the shorter step 3 renders.
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [currentStep])

  useEffect(() => {
    loadProgress()
  }, [])

  // Match <html> AND <body> background to the onboarding gradient so iOS
  // rubber-band overscroll and short-content scroll don't flash the light
  // app background below the card. Body has bg-background applied globally
  // in index.css, so we override both.
  useEffect(() => {
    const html = document.documentElement
    const body = document.body
    const prevHtml = html.style.backgroundColor
    const prevBody = body.style.backgroundColor
    html.style.backgroundColor = '#0d0a1f'
    body.style.backgroundColor = '#0d0a1f'
    return () => {
      html.style.backgroundColor = prevHtml
      body.style.backgroundColor = prevBody
    }
  }, [])

  const loadProgress = async () => {
    // Only check progress if user has a token (returning user)
    const token = localStorage.getItem('app_auth_token')
    if (!token) {
      // New user, start from beginning
      setCurrentStep(1)
      setIsLoading(false)
      return
    }

    // Check email verification status first — gate step 2+ on this
    try {
      const meRes = await api.get('/api/auth/me')
      if (meRes.data?.agent) {
        setEmailVerified(!!meRes.data.agent.emailVerified)
      }
    } catch { /* fall through */ }

    try {
      const response = await api.get('/api/onboarding/progress')
      if (response.data.success) {
        // Map the API response to our 4-step flow: Account → Org → Plan → Complete.
        // Backend post-organization states all advance to Plan step until plan is selected.
        const stepMap: Record<string, number> = {
          'account': 1,
          'organization': 2,
          'plan': 3,
          'branding': 3,
          'widget': 3,
          'voice': 3,
          'integrations': 3,
          'complete': 4,
          'done': 4,
        }
        const currentStepNum = stepMap[response.data.currentStep] || 1
        const completedStepsList: number[] = []
        const prog = response.data.progress || {}
        if (prog.account) completedStepsList.push(1)
        if (prog.organization) completedStepsList.push(2)
        // Plan step is complete once the subscription exists
        if (response.data.organization?.stripeSubscriptionId || prog.plan) {
          completedStepsList.push(3)
        }
        if (prog.complete) completedStepsList.push(4)

        setProgress({
          currentStep: currentStepNum,
          completedSteps: completedStepsList,
          data: {
            organization: response.data.organization ? {
              id: response.data.organization.id,
              name: response.data.organization.name,
              slug: response.data.organization.slug,
              website: response.data.organization.website,
              industry: response.data.organization.industry
            } : undefined
          }
        })
        setCurrentStep(currentStepNum)
        setCompletedSteps(completedStepsList)
      }
    } catch (error: any) {
      console.error('[Onboarding] Progress load failed:', error.response?.status, error.message)
      // Token invalid or expired — clear stale token so step 1 API calls don't 401
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('app_auth_token')
        setCurrentStep(1)
      } else {
        // API failed but we have a token — try localStorage fallback
        const savedStep = localStorage.getItem('app_onboarding_step')
        setCurrentStep(savedStep ? parseInt(savedStep) : 1)
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Demo redemption mode — set when the user arrived via /redeem?code=…
  // The access code lives in sessionStorage so a refresh mid-onboarding
  // still applies it. Cleared after step 2 (org creation), which is when
  // the API consumes the code.
  const isDemoRedemption = typeof window !== 'undefined' &&
    !!localStorage.getItem('app_access_code')

  const handleStepComplete = (stepId: number, data?: any) => {
    if (!completedSteps.includes(stepId)) {
      setCompletedSteps([...completedSteps, stepId])
    }

    // Store step data in progress and persist to localStorage
    if (data) {
      const key =
        stepId === 1 ? 'account' :
        stepId === 2 ? 'organization' :
        stepId === 3 ? 'plan' :
        'complete'
      const updated = { ...stepData, [key]: data }
      setStepData(updated)
      saveStepData(updated)

      setProgress(prev => ({
        currentStep: stepId + 1,
        completedSteps: prev?.completedSteps || [],
        data: {
          ...prev?.data,
          ...(stepId === 1 && { account: data }),
          ...(stepId === 2 && { organization: data }),
        }
      }))
    }

    // New signups always need verification — account step returns the flag
    if (stepId === 1 && data?.requiresEmailVerification) {
      setEmailVerified(false)
    }

    // Demo redemption: org-creation has consumed the access code, so we
    // can clear it now AND skip the Plan step (no card needed). Jump
    // directly from step 2 → step 4 (Complete).
    if (stepId === 2 && isDemoRedemption) {
      localStorage.removeItem('app_access_code')
      localStorage.removeItem('app_access_code_meta')
      localStorage.removeItem('legallady_affiliate_ref')
      localStorage.removeItem('legallady_affiliate_ref_meta')
      // Mark Plan step complete so progress UI doesn't show it as pending.
      if (!completedSteps.includes(3)) setCompletedSteps([...completedSteps, 2, 3])
      setCurrentStep(4)
      return
    }

    if (stepId === 2) {
      localStorage.removeItem('legallady_affiliate_ref')
      localStorage.removeItem('legallady_affiliate_ref_meta')
    }

    // Move to next step
    if (stepId < steps.length) {
      setCurrentStep(stepId + 1)
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleComplete = () => {
    // Clean up onboarding data from localStorage
    localStorage.removeItem(STEP_DATA_KEY)
    localStorage.removeItem('app_onboarding_step')
    localStorage.removeItem('app_onboarding_paused')
    // Land new users on AI Agents — it already shows a zero-state setup
    // banner with a Create AI Agent CTA, which is far more useful than an
    // empty dashboard of zeros. Full page nav so useAuth re-initializes
    // from the updated localStorage (token + onboardingComplete flag).
    window.location.href = '/agents'
  }

  const handleSaveAndContinueLater = () => {
    setShowSaveDialog(true)
  }

  const confirmSaveAndExit = () => {
    // Save current step and form data so they can resume
    localStorage.setItem('app_onboarding_paused', 'true')
    localStorage.setItem('app_onboarding_step', String(currentStep))
    saveStepData(stepData)

    // Navigate to login page
    navigate('/login')
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0d0a1f] to-[#12103a] flex items-center justify-center">
        <div className="animate-pulse text-white/60">Loading...</div>
      </div>
    )
  }

  const brandValueProps = [
    { icon: Phone, title: 'AI voice agents that answer 24/7', detail: 'Never miss a call — inbound and outbound handled' },
    { icon: MessageSquare, title: 'Smart chat on your website', detail: 'Engage visitors the moment they land' },
    { icon: Brain, title: 'Knowledge base built from your site', detail: 'Firm-specific answers, not generic AI' },
    { icon: BarChart3, title: 'Every interaction tracked', detail: 'Calls, chats, leads — all in one place' },
  ]

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-[#0d0a1f] to-[#12103a] overflow-hidden">
      {/* Subtle atmospheric gradient accents — prevent "empty void" feel on wide screens */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-0 right-[-20%] h-[600px] w-[600px] rounded-full blur-[140px] opacity-50"
        style={{ background: 'radial-gradient(closest-side, rgba(77, 113, 253, 0.22), transparent)' }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-[-10%] right-[20%] h-[500px] w-[500px] rounded-full blur-[120px] opacity-40"
        style={{ background: 'radial-gradient(closest-side, rgba(122, 157, 248, 0.18), transparent)' }}
      />

      <div className="relative lg:grid lg:grid-cols-[minmax(320px,380px)_1fr] min-h-screen">
        {/* ===== LEFT BRAND PANEL (desktop only) — narrower + tighter rhythm ===== */}
        <aside className="hidden lg:flex lg:flex-col lg:justify-between lg:h-screen lg:sticky lg:top-0 px-8 py-8 border-r border-white/5 bg-black/20 backdrop-blur-sm">
          <div>
            <Wordmark size="lg" variant="light" className="mb-8" />
            <h1 className="text-2xl xl:text-3xl font-heading font-semibold text-white tracking-tight leading-[1.15] mb-3">
              Your AI receptionist,<br />live in minutes.
            </h1>
            <p className="text-white/60 text-sm leading-relaxed mb-7 max-w-sm">
              Answer every call, capture every lead, and never let a potential client slip through the cracks.
            </p>
            <ul className="space-y-3.5">
              {brandValueProps.map((prop, idx) => (
                <li
                  key={prop.title}
                  className="flex items-start gap-3 opacity-0 animate-fade-in-up"
                  style={{
                    animationDelay: `${120 + idx * 60}ms`,
                    animationFillMode: 'forwards',
                    animationTimingFunction: 'cubic-bezier(0.23, 1, 0.32, 1)',
                  }}
                >
                  <div className="h-8 w-8 rounded-lg bg-brand-royal/20 border border-brand-royal/30 flex items-center justify-center shrink-0">
                    <prop.icon className="h-3.5 w-3.5 text-brand-periwinkle" strokeWidth={1.75} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white leading-snug">{prop.title}</p>
                    <p className="text-xs text-white/50 mt-0.5 leading-snug">{prop.detail}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <p className="text-xs text-white/35 leading-relaxed">
            Trusted by law firms for front-desk calls, lead capture, and client intake — powered by AI trained on your practice.
          </p>
        </aside>

        {/* ===== RIGHT STEP CONTENT — centered column, fits in viewport ===== */}
        <main className="flex flex-col px-4 py-6 sm:px-6 sm:py-10 lg:px-10 lg:py-8 xl:py-10">
          {/* Landscape-oriented card on desktop with side-by-side layout
              inside each step (heading/trust on left, form on right) so
              the card fits above the fold on typical desktop viewports.
              Compact stack on mobile. */}
          <div className="w-full max-w-2xl lg:max-w-[960px] xl:max-w-[1080px] 2xl:max-w-[1200px] mx-auto flex-1 flex flex-col">
            {/* Mobile/tablet header (desktop uses the left panel instead) */}
            <div className="lg:hidden text-center mb-6 sm:mb-8">
              <Wordmark size="lg" variant="light" className="justify-center mb-5" />
              <h1 className="text-sm sm:text-base font-medium text-white/75 tracking-wide">Let's get you set up</h1>
              <p className="text-white/45 mt-1 text-xs sm:text-sm">
                A few quick steps and you'll be live
              </p>
            </div>

            {/* Demo redemption banner — shown when the user arrived via /redeem.
                Confirms they're getting the full demo and won't see a card step. */}
            {isDemoRedemption && (() => {
              try {
                const meta = JSON.parse(localStorage.getItem('app_access_code_meta') || '{}')
                if (!meta?.label) return null
                return (
                  <div className="mb-5 rounded-lg border border-emerald-400/30 bg-emerald-500/10 px-4 py-3 text-emerald-100">
                    <p className="text-xs font-bold uppercase tracking-wider text-emerald-300 mb-1">
                      Demo · {meta.durationDays || 30} days · no card required
                    </p>
                    <p className="text-sm font-medium text-white/90">
                      Welcome, {meta.label}
                    </p>
                  </div>
                )
              } catch { return null }
            })()}

            {/* Continue Later button */}
            {currentStep > 1 && (
              <div className="flex items-center justify-end mb-3 px-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSaveAndContinueLater}
                  className="text-white/50 hover:text-white transition-colors duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97]"
                >
                  <Clock className="h-4 w-4 mr-2" strokeWidth={1.75} />
                  Save & Exit
                </Button>
              </div>
            )}

            {/* Steps Indicator — connected line pattern, tighter spacing */}
            <div className="mb-5 sm:mb-6">
              <nav aria-label="Progress">
                <ol className="flex items-center gap-0">
                  {steps.map((step, idx) => {
                    // Don't allow going back to Account (step 1) once completed — it's a one-time registration
                    const canNavigate = (completedSteps.includes(step.id) || step.id === currentStep) && !(step.id === 1 && completedSteps.includes(1))
                    const isCompleted = completedSteps.includes(step.id)
                    const isCurrent = currentStep === step.id
                    const isLast = idx === steps.length - 1
                    return (
                      <li
                        key={step.id}
                        className={`flex items-center ${isLast ? '' : 'flex-1'}`}
                      >
                        <div
                          className={`flex flex-col items-center shrink-0 ${canNavigate ? 'cursor-pointer group' : ''}`}
                          onClick={() => canNavigate && setCurrentStep(step.id)}
                        >
                          <div
                            className={`
                              flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full border-2
                              transition-[transform,background-color,border-color,color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]
                              ${canNavigate ? 'group-hover:scale-110 group-active:scale-95' : ''}
                              ${isCompleted
                                ? 'bg-brand-royal border-brand-royal text-white shadow-md shadow-brand-royal/40'
                                : isCurrent
                                  ? 'border-brand-periwinkle text-brand-periwinkle bg-brand-royal/10'
                                  : 'border-white/15 text-white/40 bg-transparent'
                              }
                            `}
                          >
                            {isCompleted ? (
                              <Check className="h-5 w-5" strokeWidth={2.5} />
                            ) : (
                              <span className="text-sm font-semibold">{step.id}</span>
                            )}
                          </div>
                          <span
                            className={`
                              mt-2 text-[11px] font-medium tracking-wide uppercase
                              ${isCurrent ? 'text-white' : canNavigate ? 'text-white/60' : 'text-white/35'}
                            `}
                          >
                            {step.name}
                          </span>
                        </div>
                        {!isLast && (
                          <div
                            className={`h-px flex-1 mx-2 sm:mx-3 -mt-5 sm:-mt-6 transition-colors duration-200
                              ${isCompleted ? 'bg-brand-royal/60' : 'bg-white/10'}`}
                          />
                        )}
                      </li>
                    )
                  })}
                </ol>
              </nav>
            </div>

            {/* Step Content — tighter padding on desktop since each step
                now splits into two columns, the card doesn't need as much
                internal breathing room. */}
            <div className="dark-form rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl shadow-2xl shadow-black/40 p-5 sm:p-7 lg:p-8 text-white [&_label]:text-white/80 [&_p]:text-white/60 [&_input]:text-white [&_input]:bg-white/10 [&_input]:border-white/20 [&_input::placeholder]:text-white/30 [&_textarea]:text-white [&_textarea]:bg-white/10 [&_textarea]:border-white/20 [&_textarea::placeholder]:text-white/30 [&_button]:border-white/20 flex-1">
                {currentStep === 1 && (
                  <CreateAccountStep
                    onComplete={(data) => handleStepComplete(1, data)}
                    initialData={progress?.data?.account}
                  />
                )}
                {currentStep === 2 && emailVerified === false && (
                  <VerifyEmailStep
                    email={stepData.account?.email || progress?.data?.account?.email || ''}
                    onVerified={() => setEmailVerified(true)}
                  />
                )}
                {currentStep === 2 && emailVerified !== false && (
                  <OrganizationSetupStep
                    onComplete={(data) => handleStepComplete(2, data)}
                    onBack={handleBack}
                    signupEmail={stepData.account?.email || progress?.data?.account?.email || ''}
                    initialData={stepData.organization || (progress?.data?.organization ? {
                      ...progress.data.organization,
                      practiceAreas: progress.data.organization.industry
                        ? progress.data.organization.industry.split(', ').filter(Boolean)
                        : []
                    } : undefined)}
                  />
                )}
                {currentStep === 3 && (
                  <PlanSelectStep
                    onComplete={(data) => handleStepComplete(3, data)}
                    onBack={handleBack}
                  />
                )}
                {currentStep === 4 && (
                  <CompleteStep onComplete={handleComplete} onBack={handleBack} />
                )}
            </div>
          </div>
        </main>
      </div>

      {/* Continue Later Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Continue Later?</DialogTitle>
            <DialogDescription>
              Your progress is saved automatically. Just sign back in to pick up where you left off.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
              <div className="flex-shrink-0">
                <Check className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-sm">Progress Saved</p>
                <p className="text-sm text-muted-foreground">
                  {completedSteps.length} of {steps.length} steps completed
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
              Keep Going
            </Button>
            <Button onClick={confirmSaveAndExit}>
              Sign Out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
